/**
 * Native-module registry (canonical spec HARD 9, 10, 11).
 *
 * PROTEX is the first native module. Before this file, `ShellPage`'s
 * non-docked branch rendered only the placeholder "This module has no view
 * yet." — a native module would never be reachable without a map from
 * `module_key` to its component. This is the minimum shell change: this file
 * plus the two lines in `ShellPage.tsx` that resolve it. Do not expand it
 * past that without treating it as a design decision (dev-loop spec,
 * Escalation Rules).
 *
 * The registry also has to let a module hand its S8 `meta` up to the
 * shell-owned `ModuleViewport` (HARD 11) — `ModuleViewport` is rendered by
 * `ShellPage`, one level above the module's own tab content, so the module
 * cannot pass its own prop to it directly. `useNativeModuleMeta` bridges that:
 * it always calls the SAME hooks in the SAME order on every render (rules of
 * hooks), gated internally by `enabled`, so `ShellPage` can call it
 * unconditionally regardless of which module — native, docked, or none — is
 * currently open.
 */
import type { JSX } from "react";

import type { FreshnessMeta } from "./protex/api";
import { DistributionModule, useDistributionMeta } from "./distribution";
import { BenchmarkModule, useBenchmarkMeta } from "./meteoric_competitor_benchmark";
import { MachineModule, useMachineMeta } from "./meteoric_machine";
import { ProfitYieldModule, useProfitYieldMeta } from "./meteoric_profit_yield";
import { ProtexModule, useProtexMeta } from "./protex";
import { UnitsSoldModule, useUnitsSoldMeta } from "./meteoric_units_sold";
import { RawMaterialsModule, useRawMaterialsMeta } from "./raw_materials";
import { FulfilmentCostsModule, useFulfilmentCostsMeta } from "./fulfilment_costs";
import { PrecisionPricingModule, usePrecisionPricingMeta } from "./precision_pricing";
import { PcogsModule, usePcogsMeta } from "./pcogs";
import { LandingPagesModule, useLandingPagesMeta } from "./landing_pages";
import { CvceMarketingModule, useCvceMarketingMeta } from "./cvce_marketing";
import { CvcePipelineModule, useCvcePipelineMeta } from "./cvce_pipeline";
import { KpiDataIntegrityModule, useKpiDataIntegrityMeta } from "./kpi_data_integrity";
import { SourcingFunnelModule, useSourcingFunnelMeta } from "./sourcing_funnel";
import { OpsModule, useOpsMeta } from "./ops";
import { CampaignsModule, useCampaignsMeta } from "./campaigns";
import { AppJourneyModule, useAppJourneyMeta } from "./app_journey";
import {
  PrecisionAdminDevModule,
  PrecisionAdminModule,
  usePrecisionAdminDevMeta,
  usePrecisionAdminDevTabHints,
  usePrecisionAdminMeta,
  usePrecisionAdminTabHints,
} from "./precision_admin";

export interface NativeModuleProps {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  /** For `PermissionGate`'s copy — named the same way `ModuleViewport` is. */
  areaName: string;
  ownerName?: string | null;
}

export type NativeModuleComponent = (props: NativeModuleProps) => JSX.Element;

const NATIVE_MODULES: Record<string, NativeModuleComponent> = {
  protex: ProtexModule,
  distribution: DistributionModule,
  meteoric_profit_yield: ProfitYieldModule,
  meteoric_competitor_benchmark: BenchmarkModule,
  meteoric_units_sold: UnitsSoldModule,
  meteoric_machine: MachineModule,
  raw_materials: RawMaterialsModule,
  fulfilment_costs: FulfilmentCostsModule,
  precision_pricing: PrecisionPricingModule,
  pcogs: PcogsModule,
  landing_pages: LandingPagesModule,
  cvce_marketing: CvceMarketingModule,
  cvce_pipeline: CvcePipelineModule,
  kpi_data_integrity: KpiDataIntegrityModule,
  sourcing_funnel: SourcingFunnelModule,
  ops: OpsModule,
  campaigns: CampaignsModule,
  precision_admin: PrecisionAdminModule,
  precision_admin_dev: PrecisionAdminDevModule,
  app_journey: AppJourneyModule,
};

/** `undefined` for any key not registered — `ShellPage` keeps its placeholder for that case. */
export function resolveNativeModule(moduleKey: string | null): NativeModuleComponent | undefined {
  if (!moduleKey) return undefined;
  return NATIVE_MODULES[moduleKey];
}

/**
 * The active native module's S8 `meta`, or `undefined` when the open module
 * is docked, unregistered, or none is open. Adding a second native module
 * means adding one more always-called `use<Module>Meta(enabled)` line here —
 * never a conditional hook call.
 */
export function useNativeModuleMeta(
  moduleKey: string | null,
  // The RESOLVED open tab. A module whose tabs read different sources maps it to
  // that tab's route so the freshness line is per tab. Every module below
  // ignores it, so their hooks and order are unchanged.
  _tabKey: string | null = null,
): FreshnessMeta | null | undefined {
  const protexMeta = useProtexMeta(moduleKey === "protex");
  const distributionMeta = useDistributionMeta(moduleKey === "distribution");
  const profitYieldMeta = useProfitYieldMeta(moduleKey === "meteoric_profit_yield");
  const benchmarkMeta = useBenchmarkMeta(moduleKey === "meteoric_competitor_benchmark");
  const unitsSoldMeta = useUnitsSoldMeta(moduleKey === "meteoric_units_sold");
  const machineMeta = useMachineMeta(moduleKey === "meteoric_machine");
  const rawMaterialsMeta = useRawMaterialsMeta(moduleKey === "raw_materials");
  const fulfilmentCostsMeta = useFulfilmentCostsMeta(moduleKey === "fulfilment_costs");
  const precisionPricingMeta = usePrecisionPricingMeta(moduleKey === "precision_pricing");
  const pcogsMeta = usePcogsMeta(moduleKey === "pcogs");
  const landingPagesMeta = useLandingPagesMeta(moduleKey === "landing_pages");
  const cvceMarketingMeta = useCvceMarketingMeta(moduleKey === "cvce_marketing");
  const cvcePipelineMeta = useCvcePipelineMeta(moduleKey === "cvce_pipeline");
  const kpiDataIntegrityMeta = useKpiDataIntegrityMeta(moduleKey === "kpi_data_integrity");
  const sourcingFunnelMeta = useSourcingFunnelMeta(moduleKey === "sourcing_funnel");
  const opsMeta = useOpsMeta(moduleKey === "ops");
  const campaignsMeta = useCampaignsMeta(moduleKey === "campaigns");
  const precisionAdminMeta = usePrecisionAdminMeta(moduleKey === "precision_admin");
  const precisionAdminDevMeta = usePrecisionAdminDevMeta(moduleKey === "precision_admin_dev");
  const appJourneyMeta = useAppJourneyMeta(moduleKey === "app_journey");
  if (moduleKey === "protex") return protexMeta;
  if (moduleKey === "distribution") return distributionMeta;
  if (moduleKey === "meteoric_profit_yield") return profitYieldMeta;
  if (moduleKey === "meteoric_competitor_benchmark") return benchmarkMeta;
  if (moduleKey === "meteoric_units_sold") return unitsSoldMeta;
  if (moduleKey === "meteoric_machine") return machineMeta;
  if (moduleKey === "raw_materials") return rawMaterialsMeta;
  if (moduleKey === "fulfilment_costs") return fulfilmentCostsMeta;
  if (moduleKey === "precision_pricing") return precisionPricingMeta;
  if (moduleKey === "pcogs") return pcogsMeta;
  if (moduleKey === "landing_pages") return landingPagesMeta;
  if (moduleKey === "cvce_marketing") return cvceMarketingMeta;
  if (moduleKey === "cvce_pipeline") return cvcePipelineMeta;
  if (moduleKey === "kpi_data_integrity") return kpiDataIntegrityMeta;
  if (moduleKey === "sourcing_funnel") return sourcingFunnelMeta;
  if (moduleKey === "ops") return opsMeta;
  if (moduleKey === "campaigns") return campaignsMeta;
  if (moduleKey === "precision_admin") return precisionAdminMeta;
  if (moduleKey === "precision_admin_dev") return precisionAdminDevMeta;
  if (moduleKey === "app_journey") return appJourneyMeta;
  return undefined;
}

/**
 * What a native module tells the shell about its tabs right now: a live count per tab (shown after
 * the tab's name) and the tab to open on when the URL names none. The tab bar stays shell-owned
 * (I-7); the module only supplies numbers. `undefined` = no hints, the registry order alone.
 * Same rule as the meta bridge: one always-called hook per module, never a conditional call.
 */
export type TabHints = { counts?: Record<string, number>; preferred?: string | null };

export function useNativeModuleTabHints(moduleKey: string | null): TabHints | undefined {
  const precisionAdmin = usePrecisionAdminTabHints(moduleKey === "precision_admin");
  const precisionAdminDev = usePrecisionAdminDevTabHints(moduleKey === "precision_admin_dev");
  if (moduleKey === "precision_admin") return precisionAdmin;
  if (moduleKey === "precision_admin_dev") return precisionAdminDev;
  return undefined;
}
